import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastTypes, GlobalToastTypeNames } from 'types';
import {
  useAttachPandadocTeacherMutation,
  useDetachPandadocTeacherMutation,
} from 'core/graphql';

import { useToasts } from 'core/hooks';

interface IPandadocTeacherLinkHook {
  /** Attach a `candidate` document to a teacher, then refetch. */
  link: (pandadocId: string, teacherId: string) => Promise<void>;
  /** Detach a manually `linked` document from its teacher, then refetch. */
  unlink: (pandadocId: string) => Promise<void>;
  /** True while either mutation is in flight. */
  loading: boolean;
}

/**
 * Reads the backend `errors` JSONString envelope (null on success, otherwise an
 * object like `{"teacher_id": "…"}`); returns the first message when present.
 */
const firstMessageOf = (obj: Record<string, unknown>): string | null => {
  const message = Object.values(obj).find((value) => Boolean(value));
  return message != null ? String(message) : null;
};

const firstError = (errors: unknown): string | null => {
  if (!errors) {
    return null;
  }
  // graphene's `JSONString` scalar arrives over the wire as JSON TEXT (e.g.
  // '{"teacher_id": "Teacher does not exist"}'), not a parsed object — parse it
  // to surface the human message instead of dumping raw JSON into the toast.
  if (typeof errors === 'string') {
    try {
      const parsed = JSON.parse(errors);
      if (parsed && typeof parsed === 'object') {
        return firstMessageOf(parsed as Record<string, unknown>) || errors;
      }
    } catch {
      // not JSON — show the string verbatim
    }
    return errors;
  }
  if (typeof errors === 'object') {
    return firstMessageOf(errors as Record<string, unknown>) || String(errors);
  }
  return String(errors);
};

/**
 * Teacher analog of `usePandadocLink` (spec 0007): wraps the
 * link/unlink-PandaDoc-teacher mutations with success/error toasts and an
 * `onDone` refetch callback. The candidate/linked affordances on the teacher
 * Договор tab call these; the tab re-queries its document connection on
 * `onDone` so the freshly (un)linked row re-renders with its new `matchType`.
 */
export const usePandadocTeacherLink = (
  onDone?: () => void,
): IPandadocTeacherLinkHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const [
    linkMutation,
    { loading: linking },
  ] = useAttachPandadocTeacherMutation();
  const [
    unlinkMutation,
    { loading: unlinking },
  ] = useDetachPandadocTeacherMutation();

  const notify = useCallback(
    (type: ToastTypes, title: string) => {
      addToast({
        data: { type, title },
        type: GlobalToastTypeNames.PandadocSync,
      });
    },
    [addToast],
  );

  const link = useCallback(
    async (pandadocId: string, teacherId: string): Promise<void> => {
      try {
        const result = await linkMutation({
          variables: { pandadocId, teacherId },
        });
        const message = firstError(result.data?.linkPandadocTeacher?.errors);
        if (message) {
          notify(
            ToastTypes.error,
            t('pages.teacherDetail.contract.linkError', { error: message }),
          );
          return;
        }
        notify(
          ToastTypes.success,
          t('pages.teacherDetail.contract.linkSuccess'),
        );
        if (onDone) onDone();
      } catch (error) {
        notify(
          ToastTypes.error,
          t('pages.teacherDetail.contract.linkError', {
            error: (error as Error).message,
          }),
        );
      }
    },
    [linkMutation, notify, t, onDone],
  );

  const unlink = useCallback(
    async (pandadocId: string): Promise<void> => {
      try {
        const result = await unlinkMutation({ variables: { pandadocId } });
        const message = firstError(result.data?.unlinkPandadocTeacher?.errors);
        if (message) {
          notify(
            ToastTypes.error,
            t('pages.teacherDetail.contract.linkError', { error: message }),
          );
          return;
        }
        notify(
          ToastTypes.success,
          t('pages.teacherDetail.contract.unlinkSuccess'),
        );
        if (onDone) onDone();
      } catch (error) {
        notify(
          ToastTypes.error,
          t('pages.teacherDetail.contract.linkError', {
            error: (error as Error).message,
          }),
        );
      }
    },
    [unlinkMutation, notify, t, onDone],
  );

  return { link, unlink, loading: linking || unlinking };
};
